/**
 * Real-record profile mappers for the Deal Dossier's Property Profile and
 * Sponsor / Developer tabs.
 *
 * Every value comes from a named source, in a fixed order of authority:
 *
 *   1. the deal record — the loan row's native columns and its custom_fields
 *      (the field catalogue), i.e. what a person or the parser stated;
 *   2. the property / sponsor master, when this deal links to one;
 *   3. external site intelligence for the address (Google, US Census, FEMA);
 *   4. "Not on record".
 *
 * A stated figure is never overwritten by a looked-up one — the record wins,
 * and the lookup only fills a gap. Where nothing answers, the section says so
 * rather than borrowing a demo value. Nothing here is invented.
 */
import type { RawDealFile } from '@/services/dealFile'
import type { PropertyProfile } from '@/types/propertyProfile'
import type {
  DossierAddress,
  DossierComparable,
  DossierPropertyMaster,
  DossierSponsorMaster,
  PropertySiteIntel,
  SponsorPortfolio,
} from '@/services/dossierContext'
import { displayedAddress, type ResolvedAddress } from '@/components/deal/dossier/addressPrecedence'
import { resolveAssetPoint } from '@/components/deal/dossier/assetCoordinate'
import { parseDateValue } from '@/utils/dates'
import { productLabel } from '@/components/deal/dossier/dealTerms'

/** Everything the dossier resolved beyond the deal record itself. */
export type ProfileContext = {
  property?: DossierPropertyMaster | null
  /** The server's account of the deal's address and where each part came from. */
  address?: DossierAddress | null
  /**
   * False while the server's account of where each address part came from has
   * not arrived (or could not be read). The deal file alone cannot tell a
   * stated street from one a name lookup wrote, so no address is printed
   * until it can. Absent means decided, as for every caller that predates it.
   */
  addressDecided?: boolean
  /**
   * The address the dossier already settled (header, map, lookups). When given
   * it is THE address: the profile does not pair the deal file with the server
   * block a second time, which during a refetch can pair one request's street
   * with the other's provenance.
   */
  settledAddress?: ResolvedAddress | null
  sponsor?: DossierSponsorMaster | null
  sponsorPortfolio?: SponsorPortfolio | null
  comparables?: DossierComparable[] | null
  intel?: PropertySiteIntel | null
  broker?: { phone?: string | null; email?: string | null; name?: string | null } | null
}

export const NOT_ON_RECORD = 'Not on record'
const DASH = '—'

/** Read a catalogue field: native column first, then custom_fields. */
function get(raw: RawDealFile, key: string): unknown {
  const native = raw[key]
  if (native !== undefined && native !== null && native !== '') return native
  const custom = raw.custom_fields
  if (custom && typeof custom === 'object') {
    const v = (custom as Record<string, unknown>)[key]
    if (v !== undefined && v !== null && v !== '') return v
  }
  return undefined
}

function text(v: unknown, fallback = DASH): string {
  if (v === null || v === undefined || v === '' || v === '—') return fallback
  return String(v)
}

function money(v: unknown, fallback = DASH): string {
  const n = typeof v === 'number' ? v : Number(v)
  if (!Number.isFinite(n) || n === 0) return fallback
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 2)}M`
  return `$${Math.round(n).toLocaleString()}`
}

function pct(v: unknown, fallback = DASH): string {
  const n = typeof v === 'number' ? v : Number(v)
  if (!Number.isFinite(n) || n === 0) return fallback
  // Parser decimals vs catalogue 0-100. The magnitude decides: `n <= 1.5`
  // read every negative figure as a decimal, and a -650.5% cap rate (itself
  // the product of a wrong value) reached the screen as -65050.43%.
  const scaled = Math.abs(n) <= 1.5 ? n * 100 : n
  return `${scaled.toFixed(scaled % 1 === 0 ? 0 : 2)}%`
}

/** A cap rate is a yield on value: above zero, and nowhere near 100%. A
 *  figure outside that is a wrong NOI or a wrong value showing through the
 *  division — the same 0-100 band the catalogue column enforces on intake. */
export function plausibleCapRate(v: unknown): number | undefined {
  const n = typeof v === 'number' ? v : Number(v)
  if (!Number.isFinite(n) || n === 0) return undefined
  const scaled = Math.abs(n) <= 1.5 ? n * 100 : n
  return scaled > 0 && scaled <= 100 ? scaled : undefined
}

/** Asset classes whose rent roll is a list of dwelling units — where the
 *  roll's length IS the unit count and rent per SF is quoted by the month. */
const RESIDENTIAL_RE =
  /multi-?\s?family|apartment|residential|student|senior|housing|manufactured|mobile home|build[-\s]?to[-\s]?rent|\bbtr\b|\bsfr\b|single[-\s]?family|condo/i

/** Statuses that say a unit earns nothing today. */
const NOT_LET_RE = /\b(vacant|vacancy|non[-\s]?revenue|model|down|available|employee|admin)\b/i

/**
 * Let, by the roll's own account: its Status column first, its rent second.
 * A sponsor's underwriting roll carries a rent on a VACANT line (the asking
 * rent), so "has a rent" counted all 24 of The Chelsea's vacant units as
 * occupied and put their asking rents into the in-place total.
 */
export function isOccupiedRow(row: { status?: string | null; rentMo?: number | null }): boolean {
  const status = (row.status ?? '').trim()
  if (status && status !== DASH) return !NOT_LET_RE.test(status)
  return (row.rentMo ?? 0) > 0
}

function num(v: unknown): number | undefined {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

/**
 * The address the deal shows, by the one order every surface uses
 * (`addressPrecedence.ts`): the deal's own parts as a person or the package
 * stated them, then the property master. A part a name lookup wrote is
 * withheld. `full` is '' when none of them states one.
 */
function recordedAddress(raw: RawDealFile, ctx: ProfileContext): ResolvedAddress {
  if (ctx.settledAddress) return ctx.settledAddress
  return displayedAddress({
    deal: {
      street: text(get(raw, 'street'), ''),
      city: text(get(raw, 'city'), ''),
      state: text(get(raw, 'state'), ''),
      zip: text(get(raw, 'zip'), ''),
    },
    location: text(get(raw, 'location'), ''),
    master: ctx.property,
    server: ctx.address,
  })
}

function leaseExpirationsFrom(
  tenants: { leaseEnd: string; sf: number }[],
): { year: string; sf: number; pctOfTotal: number }[] {
  const buckets = new Map<string, number>()
  let total = 0
  for (const t of tenants) {
    // A lease ending 2027-01-01 read as UTC midnight fell into 2026 west of UTC.
    const d = parseDateValue(t.leaseEnd)
    if (!d) continue
    const year = String(d.getFullYear())
    const weight = t.sf > 0 ? t.sf : 1
    buckets.set(year, (buckets.get(year) ?? 0) + weight)
    total += weight
  }
  if (total === 0) return []
  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([year, sf]) => ({
      year,
      sf,
      pctOfTotal: Math.round((sf / total) * 1000) / 10,
    }))
}

function waltYears(tenants: { leaseEnd: string; sf: number }[]): number | undefined {
  const now = Date.now()
  let weighted = 0
  let total = 0
  for (const t of tenants) {
    const d = parseDateValue(t.leaseEnd)
    if (!d) continue
    const years = (d.getTime() - now) / (365.25 * 24 * 3600 * 1000)
    if (years < 0) continue
    const weight = t.sf > 0 ? t.sf : 1
    weighted += years * weight
    total += weight
  }
  if (total === 0) return undefined
  return Math.round((weighted / total) * 10) / 10
}


/** First value that is actually present, in the order the caller ranked them. */
function firstOf<T>(...candidates: (T | null | undefined)[]): T | undefined {
  for (const value of candidates) {
    if (value === null || value === undefined) continue
    if (typeof value === 'string' && value.trim() === '') continue
    if (typeof value === 'number' && !Number.isFinite(value)) continue
    return value
  }
  return undefined
}

/** A source answered but had nothing, or was never asked — say which. */
function sourceNote(intel: PropertySiteIntel | null | undefined, name: string): string {
  if (!intel) return NOT_ON_RECORD
  const report = intel.sources.find((s) => s.source === name)
  if (!report) return NOT_ON_RECORD
  if (report.ok) return report.detail ?? NOT_ON_RECORD
  return report.detail ?? NOT_ON_RECORD
}

function integer(v: unknown): string | undefined {
  // Number(null) and Number('') are 0: an absent figure printed "0" parking
  // spaces on every deal whose property master had none (PROP-04).
  if (v == null || (typeof v === 'string' && v.trim() === '')) return undefined
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? Math.round(n).toLocaleString() : undefined
}

function signedPct(v: number | null | undefined): string | undefined {
  if (typeof v !== 'number' || !Number.isFinite(v)) return undefined
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`
}

function nearbyLine(places: { name: string; vicinity: string | null }[] | undefined): string | undefined {
  if (!places?.length) return undefined
  return places.slice(0, 4).map((p) => p.name).join(' · ')
}

/** Comps from our own database, shaped for the Property page's comp table. */
function comparablesFrom(rows: DossierComparable[] | null | undefined) {
  if (!rows?.length) return []
  return rows.map((row) => ({
    name: row.propertyName,
    units: row.numberOfUnits ?? undefined,
    sf: row.unitsOrSf ?? undefined,
    // A comp table reads rent per unit; the comp database records the trade,
    // so the honest per-unit figure here is what the buyer paid, not a rent
    // we never observed.
    avgRent: row.pricePerUnit ?? 0,
    capRate: typeof row.capRate === 'number' ? (row.capRate <= 1.5 ? row.capRate * 100 : row.capRate) : 0,
    distance: [row.city, row.state].filter(Boolean).join(', ') || (row.submarket ?? DASH),
  }))
}

function compSummaryOf(rows: DossierComparable[] | null | undefined): string {
  if (!rows?.length) return NOT_ON_RECORD
  const caps = rows
    .map((r) => r.capRate)
    .filter((c): c is number => typeof c === 'number' && c > 0)
    .map((c) => (c <= 1.5 ? c * 100 : c))
  const dates = rows.map((r) => r.transactionDate).filter((d): d is string => Boolean(d)).sort()
  const parts = [`${rows.length} recorded ${rows.length === 1 ? 'transaction' : 'transactions'}`]
  if (caps.length) {
    const avg = caps.reduce((a, b) => a + b, 0) / caps.length
    parts.push(`average cap rate ${avg.toFixed(2)}% across ${caps.length}`)
  }
  if (dates.length) parts.push(`most recent ${dates[dates.length - 1]}`)
  return `${parts.join('; ')}.`
}

/** Record first, master second, external lookup third, then "Not on record". */
export function loanToPropertyProfile(
  raw: RawDealFile,
  context: ProfileContext = {},
): PropertyProfile {
  const master = context.property ?? null
  const intel = context.intel ?? null
  // What address the deal shows, and whether anything supports it. The point
  // is a point on that address only: the server says when the master's
  // coordinate was resolved for some other address, a city centre is not the
  // building, and an address nothing supports gets no coordinate at all.
  const shownAddress = recordedAddress(raw, context)
  const assetPoint = resolveAssetPoint({
    addressBlock: context.address,
    resolved: shownAddress,
    master,
    intel,
  })
  const unverifiedAddress = assetPoint.status === 'unverified' || context.addressDecided === false
  const cashFlows = (get(raw, 'cash_flows') ?? {}) as Record<string, unknown>
  const market = (get(raw, 'market_data') ?? {}) as Record<string, unknown>
  // The deal's own field first, every time. `cash_flows` is the promotion-time
  // snapshot: a corrected occupancy still printed the old figure in the comps
  // Subject rows, and a corrected revenue the old revenue (UI-12) — the cap
  // rate below already read the deal first for the same reason.
  const rev = get(raw, 'gross_revenue') ?? cashFlows.grossRevenue ?? cashFlows.revenue
  const exp = get(raw, 'operating_expenses')
  const noi = get(raw, 'noi') ?? cashFlows.prospectiveNOI
  // The deal's own cap rate. `market_data.capRate` is market data — preferring
  // it showed The Marlowe's promotion-time 7.03% copy over the deal's field.
  const capRate = get(raw, 'cap_rate')
  const occ = get(raw, 'occupancy') ?? cashFlows.occupancy ?? master?.currentOccupancy
  const statedUnits = num(get(raw, 'units')) ?? num(master?.units)
  const sf = num(get(raw, 'sf')) ?? num(master?.squareFeet)
  const uom = text(get(raw, 'unit_of_measure'), '')
  const yearBuilt = num(get(raw, 'year_built')) ?? num(master?.yearBuilt)
  // The deal's own rent roll if one was stated or parsed; otherwise the
  // property master's, which the asset-management side maintains.
  const rentRollRaw = get(raw, 'rent_roll') ?? (master?.rentRoll?.length ? master.rentRoll : undefined)
  const rentRoll = Array.isArray(rentRollRaw)
    ? rentRollRaw.map((r) => {
        const row = (r ?? {}) as Record<string, unknown>
        return {
          tenant: text(row.tenant ?? row.name),
          suite: text(row.suite ?? row.unit, DASH),
          sf: num(row.sf) ?? 0,
          rentMo: num(row.rentMo ?? row.monthlyRent ?? row.rent) ?? 0,
          leaseStart: text(row.leaseStart ?? row.lease_start, DASH),
          leaseEnd: text(row.leaseEnd ?? row.expiration ?? row.lease_end, DASH),
          status: text(row.status, DASH),
          unitType: text(row.unitType ?? row.unit_type ?? row.floorplan, '') || undefined,
          marketRent: num(row.marketRent ?? row.market_rent) ?? undefined,
          rentAnnual: num(row.rentAnnual ?? row.rent_annual ?? row.annualRent) ?? undefined,
          rentPsf: num(row.rentPsf ?? row.rent_psf ?? row.rentPerSf) ?? undefined,
          termMonths: num(row.termMonths ?? row.term_months) ?? undefined,
          rehab: text(row.rehab ?? row.renovated, '') || undefined,
        }
      })
    : []
  // A residential roll lists every dwelling, so when nobody stated a unit
  // count the roll's length is one — The Chelsea showed "317 units" under the
  // table and "—" in both Units tiles above it. A commercial roll lists
  // tenants, not units, and is never counted this way.
  const residential = RESIDENTIAL_RE.test(text(get(raw, 'asset_type'), ''))
  const unitsFromRentRoll = !statedUnits && residential && rentRoll.length ? rentRoll.length : undefined
  const units = statedUnits ?? unitsFromRentRoll
  // A stated count the residential roll contradicts (Calloway: 180 stated,
  // 312 on the roll) is shown with the variance, and nothing is divided by
  // either count until a person settles which is right (PROP-01).
  const unitCountVariance =
    statedUnits && residential && rentRoll.length && rentRoll.length !== statedUnits
      ? { stated: statedUnits, rentRoll: rentRoll.length }
      : undefined
  const unitsLabel = units
    ? `${units.toLocaleString()} ${uom || 'units'}`
    : sf ? `${sf.toLocaleString()} SF` : ''
  const perUnitBase = unitCountVariance ? undefined : units ?? sf
  const perUnit = (v: unknown) => {
    const n = typeof v === 'number' ? v : Number(v)
    if (!perUnitBase || !Number.isFinite(n) || n === 0) return DASH
    return `$${Math.round(n / perUnitBase).toLocaleString()}${units ? '/unit' : '/SF'}`
  }
  const sponsor = text(get(raw, 'sponsor'), '')
  const broker = text(get(raw, 'broker_name'), '')
  const assignee = text(get(raw, 'assignee'), '')
  // The location text goes through the same withholding as every other address
  // surface: the market and the crime panel read the state off it, and the raw
  // `location` of a legacy deal is the Places-composed street under an invented
  // city.
  const location = unverifiedAddress ? DASH : shownAddress.full || DASH
  const assetType = text(get(raw, 'asset_type'), DASH)
  const yearRenovated = num(get(raw, 'year_renovated')) ?? num(master?.yearRenovated)
  const avgUnitSizeSf = units && sf && !unitCountVariance ? Math.round(sf / units) : undefined
  // In-place rent: what the LET units pay. A vacant line's rent is an asking
  // rent, and summing it overstated the month by every vacancy on the roll.
  const letRows = rentRoll.filter(isOccupiedRow)
  const monthlyRentTotal = rentRoll.length
    ? letRows.reduce((sum, t) => sum + (t.rentMo || 0), 0)
    : undefined
  // Rent per SF is the rent roll's own total over the AREA THAT ROLL COVERS,
  // never over the whole building. A roll that lists a handful of units
  // against a 168,000 SF asset would otherwise report $0.07/SF — arithmetic
  // that is clean, an order of magnitude wrong, and never questioned. Only
  // rows stating BOTH a rent and an area enter either side of the division.
  const priced = letRows.filter((t) => t.rentMo > 0 && t.sf > 0)
  const pricedRent = priced.reduce((sum, t) => sum + t.rentMo, 0)
  const pricedSf = priced.reduce((sum, t) => sum + t.sf, 0)
  // The figure carries its period. Apartments quote rent per SF by the month
  // and every commercial class by the year; a bare "$1.47" read as wrong to
  // anyone expecting the other convention.
  const avgRentPerSf =
    pricedRent > 0 && pricedSf > 0
      ? residential
        ? `$${(pricedRent / pricedSf).toFixed(2)} /mo`
        : `$${((pricedRent * 12) / pricedSf).toFixed(2)} /yr`
      : undefined
  const stabilizedNoiValue = num(get(raw, 'noi_stabilized'))
  const stabilizedValueAmount = num(get(raw, 'value_stabilized'))
  const stabilizedCapRate =
    stabilizedNoiValue && stabilizedValueAmount
      ? `${((stabilizedNoiValue / stabilizedValueAmount) * 100).toFixed(2)}%`
      : undefined

  const lotSizeFromMaster =
    typeof master?.lotSizeAcres === 'number' && master.lotSizeAcres > 0
      ? `${master.lotSizeAcres.toLocaleString()} acres`
      : undefined

  return {
    dealId: String(raw.id),
    propertyName: text(get(raw, 'property_name'), 'Untitled Deal'),
    tagline: [unitsLabel, assetType !== DASH ? assetType : '', yearBuilt ? `Built ${yearBuilt}` : '']
      .filter(Boolean).join(' | '),
    location,
    // The address by the one precedence (person, package, master); when none
    // states one, the address Google normalised for the same deal. An address
    // nothing supports is not printed at all, and neither is Google's
    // rendering of it: both would be the guess in a nicer typeface.
    fullAddress: unverifiedAddress
      ? DASH
      : firstOf(
          shownAddress.full,
          intel?.formattedAddress ?? undefined,
          master?.address ?? undefined,
        ) ?? DASH,
    loanAmount: money(get(raw, 'loan_amount')),
    // loan_type first, as every dossier tab reads it (productLabel).
    product: text(productLabel((name) => get(raw, name)), DASH),
    stage: text(get(raw, 'stage'), DASH),
    propertyType: assetType,
    yearBuilt: yearBuilt ?? 0,
    yearRenovated,
    totalUnits: units,
    unitsFromRentRoll: unitsFromRentRoll != null,
    unitCountVariance,
    totalSF: sf ? sf.toLocaleString() : text(get(raw, 'building_area'), NOT_ON_RECORD),
    lotSize: firstOf(
      text(get(raw, 'site_area'), ''),
      lotSizeFromMaster,
    ) ?? NOT_ON_RECORD,
    avgUnitSize: avgUnitSizeSf ? `${avgUnitSizeSf.toLocaleString()} SF` : undefined,
    stories: num(get(raw, 'stories')) ?? num(master?.stories) ?? 0,
    parking: firstOf(
      text(get(raw, 'parking_spaces'), ''),
      integer(master?.parkingSpaces),
    ) ?? NOT_ON_RECORD,
    amenities: text(get(raw, 'property_description'), NOT_ON_RECORD),
    zoning: text(get(raw, 'zoning'), NOT_ON_RECORD),
    parcel: text(get(raw, 'parcel_id'), NOT_ON_RECORD),
    lastSale: money(get(raw, 'purchase_price'), NOT_ON_RECORD),
    // Google's administrative_area_level_2, or the FCC census-area answer for
    // the same coordinate — never guessed from the city.
    county: firstOf(text(get(raw, 'county'), ''), intel?.county ?? undefined) ?? NOT_ON_RECORD,
    monthlyRentTotal,
    avgRentPerSF: avgRentPerSf,
    stabilizedNoiValue,
    stabilizedValueAmount,
    stabilizedCapRate,
    photos: [],                       // real photos come from the deal file (C5) — never stock
    // The building's coordinate, and only that: 0/0 means "none". A city
    // centre, a point resolved for some other address, and any point under an
    // address nothing supports are all "none" here (see assetCoordinate.ts).
    mapLat: !unverifiedAddress && assetPoint.status === 'asset' ? assetPoint.lat ?? 0 : 0,
    mapLng: !unverifiedAddress && assetPoint.status === 'asset' ? assetPoint.lng ?? 0 : 0,
    // ACS publishes county aggregates, not radius rings, so the figure is
    // labelled with the geography it actually counts.
    population3mi: intel?.population != null
      ? `${intel.population.toLocaleString()}${intel.populationGeography ? ` (${intel.populationGeography})` : ''}`
      : sourceNote(intel, 'US Census ACS'),
    medianHHI: intel?.medianHouseholdIncome != null
      ? `$${intel.medianHouseholdIncome.toLocaleString()}`
      : sourceNote(intel, 'US Census ACS'),
    popGrowth: signedPct(intel?.populationGrowthPct) ?? sourceNote(intel, 'US Census ACS'),
    // No walk-score or crime provider is connected; an unconnected source must
    // read as unconnected rather than as a score of zero.
    walkScore: NOT_ON_RECORD, crimeIndex: NOT_ON_RECORD, empGrowth: NOT_ON_RECORD,
    topEmployers: [],
    schools: nearbyLine(intel?.schools) ?? sourceNote(intel, 'Google Places Nearby'),
    transit: nearbyLine(intel?.transit) ?? sourceNote(intel, 'Google Places Nearby'),
    nearbyDevelopments: NOT_ON_RECORD,
    submarket: firstOf(
      text(get(raw, 'msa'), ''),
      text(get(raw, 'property_msa'), ''),
      typeof (market as { msa?: unknown }).msa === 'string'
        ? (market as { msa: string }).msa
        : '',
      master?.submarket ?? undefined,
      intel?.neighborhood ?? undefined,
    ) ?? NOT_ON_RECORD,
    // The OM's market prose, promoted onto the deal. Its own field: it stood
    // in as the "Submarket" name, where a paragraph is not a submarket (UI-01).
    marketNarrative: text(get(raw, 'market_narrative'), '') || undefined,
    submarketVacancy: DASH, submarketRentGrowth: DASH, submarketAbsorption: DASH,
    revenue: money(rev, NOT_ON_RECORD),
    expenses: money(exp, NOT_ON_RECORD),
    noi: money(noi, NOT_ON_RECORD),
    capRate: pct(plausibleCapRate(capRate), NOT_ON_RECORD),
    occupancy: pct(occ, NOT_ON_RECORD),
    expenseRatio: pct(get(raw, 'expense_ratio'), DASH),
    revPerUnit: perUnit(rev),
    expPerUnit: perUnit(exp),
    noiPerUnit: perUnit(noi),
    tenants: rentRoll.length ? rentRoll : undefined,
    avgLeaseTerm: firstOf(
      text(get(raw, 'avg_lease_term'), ''),
      waltYears(rentRoll) != null ? `${waltYears(rentRoll)} yrs` : undefined,
    ),
    leaseExpirations: leaseExpirationsFrom(rentRoll),
    contacts: [
      ...(sponsor ? [{ role: 'Sponsor', name: sponsor, company: '', phone: text(get(raw, 'sponsor_contact'), ''), email: '' }] : []),
      ...(broker ? [{
        role: 'Broker / Intermediary',
        name: broker,
        company: text(get(raw, 'brokerage_company'), ''),
        phone: firstOf(
          text(get(raw, 'broker_phone'), ''),
          context.broker?.phone ?? undefined,
        ) ?? '',
        email: firstOf(
          text(get(raw, 'broker_contact_email'), ''),
          text(get(raw, 'broker_email'), ''),
          context.broker?.email ?? undefined,
        ) ?? '',
      }] : []),
      ...(assignee ? [{ role: 'Responsible Party', name: assignee, company: '', phone: '', email: '' }] : []),
    ],
    comparables: comparablesFrom(context.comparables),
    compSummary: compSummaryOf(context.comparables),
    // Condition and environmental findings come from a report a person
    // uploads, so they stay unrecorded until one exists — the platform will
    // not infer a building's condition from anything.
    lastInspection: NOT_ON_RECORD, inspector: NOT_ON_RECORD, overallCondition: NOT_ON_RECORD,
    immediateRepairs: NOT_ON_RECORD, deferredMaintenance: NOT_ON_RECORD,
    environmental: NOT_ON_RECORD, seismicZone: NOT_ON_RECORD,
    // FEMA's National Flood Hazard Layer at the site coordinate.
    floodZone: firstOf(
      text(get(raw, 'flood_zone'), ''),
      intel?.floodZone
        ? `${intel.floodZone}${intel.floodZoneDetail ? ` — ${intel.floodZoneDetail}` : ''}`
        : undefined,
    ) ?? sourceNote(intel, 'FEMA NFHL'),
    sponsorId: sponsor,
  }
}
